'use client';

import {
  TextArea,
  TextAreaAndInvalidIcon,
  TextAreaContainer,
  TextAreaContainerText,
  TextAreaError,
  TextAreaInvalidIcon,
  ValueContainer,
} from './TextAreaStyles';
import { TextAreaProps } from './types';
import { useSharedForwardRef } from '@midwest/web/base';
import {
  FormComponentLayout,
  HelperText,
  InputContainer,
  InputContainerCursor,
  LabelAndLink,
} from '@midwest/web/shared';
import { forwardRef, useId, useState } from 'react';

/**
 ```jsx
 import { MDSTextArea } from '@midwest/web/forms';
 ```
 * A text field that users can type into. Usually free form input but designers can apply restrictions with validation.
 */
export const MDSTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      name,
      label,
      disabled = false,
      linkLabel,
      linkHref,
      helperText,
      placeholder,
      errorText,
      invalid,
      required = false,
      readOnly = false,
      rows = 4,
      onChange,
      onBlur,
      value,
      defaultValue,
      validationText,
      maxLength,
      ...rest
    }: TextAreaProps,
    ref,
  ) => {
    const onChangePreventDefault = (
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      if (onChange) {
        onChange(event);
      }

      event.preventDefault();
    };

    const [focused, setFocused] = useState(false);
    const [textAreaRef, setRef] = useSharedForwardRef<HTMLTextAreaElement>(ref);

    /* Create state variable to detect when input is clicked into to differentiate focus and focus-visible styles with wrapping InputContainer component */
    const [inputClicked, setInputClicked] = useState(false);

    const handleClick = () => {
      textAreaRef?.current?.focus();
      setFocused(true);
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      setInputClicked(false);
      if (onBlur) {
        onBlur(event);
      }
    };

    const inputId = useId();
    const errorIdHook = useId();
    const describedByIdHook = useId();

    const errorId = errorText ? errorIdHook : undefined;
    const describedById = helperText ? describedByIdHook : undefined;
    const ariaDescribedBy = [describedById, errorId].reduce((prev, id) => {
      if (!id) {
        return prev;
      }
      return prev ? `${prev} ${id}` : `${id}`;
    }, undefined);

    return (
      <FormComponentLayout {...rest}>
        <LabelAndLink
          label={label}
          linkLabel={linkLabel}
          linkHref={linkHref}
          disabled={disabled}
          readOnly={readOnly}
          focused={focused}
          validationText={validationText}
          inputId={inputId}
        />

        {!!helperText && (
          <HelperText id={describedById}>{helperText}</HelperText>
        )}
        <InputContainerCursor
          data-testid="control-cursor"
          $disabled={disabled}
          $readOnly={readOnly}
        >
          <InputContainer
            data-testid="control"
            $disabled={disabled}
            $invalid={invalid}
            $focused={focused}
            $readOnly={readOnly}
            $additionalStyles={{
              height: 'auto',
              padding: readOnly ? '2px' : '0px',
            }}
            /* If we detect focus-within and textarea has not been clicked render keyboard focus border */
            $inputClicked={inputClicked}
            onMouseDown={() => {
              setInputClicked(true);
              handleClick();
            }}
          >
            <TextAreaContainer>
              <TextAreaAndInvalidIcon>
                <TextAreaContainerText>
                  <TextArea
                    ref={setRef}
                    $disabled={disabled}
                    disabled={disabled}
                    placeholder={placeholder}
                    rows={rows}
                    onChange={onChangePreventDefault}
                    onBlur={handleBlur}
                    onClick={handleClick}
                    id={inputId}
                    aria-invalid={invalid}
                    aria-required={required}
                    aria-errormessage={errorId}
                    aria-describedby={ariaDescribedBy}
                    data-dd-action-name={label}
                    name={name}
                    aria-label={
                      readOnly
                        ? `read only ${label}`
                        : maxLength
                          ? `${label} with an allowed maximum of ${maxLength} characters`
                          : label
                    }
                    value={value}
                    defaultValue={defaultValue}
                    readOnly={readOnly}
                    $readOnly={readOnly}
                    aria-readonly={readOnly}
                    maxLength={maxLength}
                  ></TextArea>
                </TextAreaContainerText>
                <div>
                  <div>
                    {' '}
                    {invalid && (
                      <TextAreaInvalidIcon
                        aria-hidden={true}
                        data-testid="invalidIcon"
                      />
                    )}
                  </div>
                </div>
              </TextAreaAndInvalidIcon>

              {!!maxLength && value !== undefined && (
                <ValueContainer>
                  {value ? value.length : 0}/{maxLength} characters
                </ValueContainer>
              )}
            </TextAreaContainer>
          </InputContainer>
        </InputContainerCursor>
        {!!errorText && (
          <TextAreaError id={errorId} role={'status'}>
            {errorText}
          </TextAreaError>
        )}
      </FormComponentLayout>
    );
  },
);

MDSTextArea.displayName = 'MDSTextArea';
